import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Button,
  Heading,
  Text,
  Select,
  Input,
  useDisclosure,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useColorModeValue,
  ButtonGroup,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { Table, LayoutGrid, BarChart2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useMetrics } from './useMetrics';
import { useIssues } from '../Issues/useIssues';
import { useRocks } from '../Rocks/useRocks';
import { useDepartments } from '../Departments/useDepartments';
import { AddMetricModal } from './components/AddMetricModal';
import { ScorecardWeeklyGrid } from './components/ScorecardWeeklyGrid';
import { ScorecardTableView } from './components/ScorecardTableView';
import { ScorecardSummaryStats } from './components/ScorecardSummaryStats';
import { Metric, MetricGoal } from '../types';
import { 
  getCurrentQuarter, 
  getCurrentYear, 
  WeekColumn,
  formatMetricValue 
} from '../utils/scorecardCalculations';

/**
 * Scorecard Page - Main page for viewing and managing Metrics typically in a 13-week trailing view.
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function ScorecardPage() {
  const { currentUser } = useAuth();
  
  // Hooks for data
  const {
    metrics,
    metricGoals,
    users,
    metricNames,
    loading,
    addMetric,
    updateMetric,
    deleteMetric,
    updateMetricGoal,
    addMetricGoal,
    getGoalsForMetric,
  } = useMetrics(currentUser?.uid);

  const { createIssue } = useIssues(currentUser?.uid);
  const { rocks } = useRocks(currentUser?.uid);
  const { departments } = useDepartments();

  // State
  const [selectedQuarter, setSelectedQuarter] = useState<number>(getCurrentQuarter());
  const [selectedYear, setSelectedYear] = useState<number>(getCurrentYear());
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'graph'>('grid');
  const [metricToEdit, setMetricToEdit] = useState<Metric | null>(null);
  
  const [filters, setFilters] = useState({
    category: 'all',
    search: '',
    ownerId: 'all',
  });

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();

  const toast = useToast();
  
  // Theme colors
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const filterBg = useColorModeValue('white', 'gray.700');

  // Derived state: Filtered metrics
  const filteredMetrics = useMemo(() => {
    return metrics.filter((metric) => {
      // 1. Filter by Quarter/Year
      // Note: Typically metrics are defined per quarter in EOS, 
      // but in this system they might persist. 
      // If the metric has explicit quarter/year, we check it.
      if (metric.quarter) {
        const metricQ = parseInt(metric.quarter.replace('Q', ''));
        if (metricQ !== selectedQuarter) return false;
      }
      if (metric.year && metric.year !== selectedYear) return false;

      // 2. Filter by Category
      if (filters.category !== 'all' && metric.category !== filters.category) return false;
      
      // 3. Filter by Owner
      if (filters.ownerId !== 'all' && metric.ownerId !== filters.ownerId) return false;

      // 4. Search
      if (
        filters.search &&
        !metric.metricName?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !metric.measurable.toLowerCase().includes(filters.search.toLowerCase())
      ) {
        return false;
      }
      
      return true;
    });
  }, [metrics, selectedQuarter, selectedYear, filters]);

  // Handlers
  const handleSaveMetric = async (formData: any) => {
    try {
      if (formData.id) {
        // Update existing metric
        await updateMetric(formData.id, formData);
        toast({
          title: 'Metric updated',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        // Create new metric
        // Inject current quarter/year if not provided (though modal usually handles this)
        const dataWithContext = {
          ...formData,
          quarter: `Q${selectedQuarter}`,
          year: selectedYear,
        };
        
        await addMetric(dataWithContext);
        toast({
          title: 'Metric created',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
      onFormClose();
      setMetricToEdit(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save metric',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEditMetric = (metric: Metric) => {
    setMetricToEdit(metric);
    onFormOpen();
  };

  const handleDeleteMetric = async (metric: Metric) => {
    if (window.confirm(`Are you sure you want to delete "${metric.metricName}"?`)) {
      try {
        await deleteMetric(metric.id);
        toast({
          title: 'Metric deleted',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to delete metric',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleUpdateGoal = async (
    metricId: string,
    periodNumber: number,
    value: number | null,
    notes: string,
    dateStart: Date,
    dateEnd: Date,
    goalId?: string
  ) => {
    try {
      if (goalId) {
        // Update existing goal
        await updateMetricGoal(goalId, {
          actualValue: value === null ? undefined : value,
          notes,
        });
      } else {
        // Create new goal
        await addMetricGoal({
          metricId,
          periodType: 'week',
          periodNumber,
          actualValue: value === null ? undefined : value,
          dateStart,
          dateEnd,
          notes,
          // We don't necessarily set goalValue here unless we want to lock it in
          // relying on metric's default goal in the UI calculation usually
        });
      }
      
      toast({
        title: 'Scorecard updated',
        status: 'success',
        duration: 1000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'Failed to update scorecard',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDropToIssue = async (metric: Metric, week: WeekColumn, actual: number | null, goal: number) => {
    try {
      const description = `Week ${week.weekNumber} (${week.label}): Actual ${formatMetricValue(actual, metric.measurementType || 'count', metric.unit)} vs Goal ${formatMetricValue(goal, metric.measurementType || 'count', metric.unit)}`;

      await createIssue({
        name: `${metric.metricName || metric.measurable} Off Track`,
        description: description,
        priority: 'medium',
        status: 'open',
        ownerId: metric.ownerId,
        assignedToUserId: metric.ownerId,
        relatedMetricId: metric.id,
        isMetric: true,
        departmentId: metric.departmentId, 
      });

      toast({
        title: 'Issue Created',
        description: 'Metric dropped to issues list',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>Loading scorecard...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={6}>
        <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={6}>
          <HStack spacing={8} align="center" flexWrap="wrap">
            <Box>
              <Heading size="lg">Scorecard</Heading>
              <Text color={subheadingColor} mt={1}>
                Track key business metrics and performance indicators
              </Text>
            </Box>

            <HStack spacing={4}>
              <ButtonGroup size="sm" isAttached variant="outline">
                <Button 
                  onClick={() => setViewMode('table')}
                  colorScheme={viewMode === 'table' ? 'gray' : 'gray'}
                  variant={viewMode === 'table' ? 'solid' : 'outline'}
                  bg={viewMode === 'table' ? 'gray.200' : 'transparent'}
                >
                  Table
                </Button>
                <Button 
                  onClick={() => setViewMode('graph')}
                  colorScheme={viewMode === 'graph' ? 'gray' : 'gray'}
                  variant={viewMode === 'graph' ? 'solid' : 'outline'}
                  bg={viewMode === 'graph' ? 'blue.500' : 'transparent'}
                  color={viewMode === 'graph' ? 'white' : 'inherit'}
                  _hover={{ bg: viewMode === 'graph' ? 'blue.600' : 'gray.100' }}
                  isDisabled // Placeholder
                >
                  Graph
                </Button>
                <Button 
                  onClick={() => setViewMode('grid')}
                  colorScheme={viewMode === 'grid' ? 'gray' : 'gray'}
                  variant={viewMode === 'grid' ? 'solid' : 'outline'}
                  bg={viewMode === 'grid' ? 'gray.200' : 'transparent'}
                >
                  13-Week Grid
                </Button>
              </ButtonGroup>
              <Button leftIcon={<AddIcon />} size="sm" colorScheme="green" onClick={() => {
                setMetricToEdit(null);
                onFormOpen();
              }}>
                Add Metric
              </Button>
            </HStack>
          </HStack>

          {/* Stats on the Right */}
          <ScorecardSummaryStats
            metrics={filteredMetrics}
            metricGoals={metricGoals}
            quarter={selectedQuarter}
            year={selectedYear}
          />
        </Flex>



        <Box mb={6} p={4} bg={filterBg} borderRadius="md" shadow="sm">
          <Flex gap={4} wrap="wrap" align="end">
             <Box>
              <Text fontSize="sm" mb={1} fontWeight="medium">Quarter</Text>
              <Select 
                size="sm" 
                value={selectedQuarter} 
                onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                width="80px"
              >
                <option value={1}>Q1</option>
                <option value={2}>Q2</option>
                <option value={3}>Q3</option>
                <option value={4}>Q4</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="sm" mb={1} fontWeight="medium">Year</Text>
              <Input 
                size="sm" 
                type="number" 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                width="100px"
              />
            </Box>

            <Box>
              <Text fontSize="sm" mb={1} fontWeight="medium">Category</Text>
              <Select
                size="sm"
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                width="150px"
              >
                <option value="all">All Categories</option>
                {['Sales', 'Marketing', 'Operations', 'Finance', 'Technology', 'Customer Service', 'Leadership'].map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Select>
            </Box>

             <Box>
              <Text fontSize="sm" mb={1} fontWeight="medium">Owner</Text>
              <Select
                size="sm"
                value={filters.ownerId}
                onChange={(e) => setFilters({ ...filters, ownerId: e.target.value })}
                width="150px"
              >
                <option value="all">All Owners</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </Box>
            
            <Box flex={1}>
                 <Text fontSize="sm" mb={1} fontWeight="medium">Search</Text>
                 <Input 
                    size="sm" 
                    placeholder="Search metrics..." 
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                 />
            </Box>
          </Flex>
        </Box>

        {viewMode === 'table' ? (
          <ScorecardTableView
            metrics={filteredMetrics}
            metricGoals={metricGoals}
            users={users}

            quarter={selectedQuarter}
            year={selectedYear}
            onEdit={handleEditMetric}
            onDelete={handleDeleteMetric}
          />
        ) : (
          <ScorecardWeeklyGrid
            metrics={filteredMetrics}
            metricGoals={metricGoals}
            quarter={selectedQuarter}
            year={selectedYear}
            onUpdateGoal={handleUpdateGoal}
            onDropToIssue={handleDropToIssue}
            users={users}
          />
        )}
      </Box>

      <AddMetricModal
        isOpen={isFormOpen}
        onClose={() => {
          onFormClose();
          setMetricToEdit(null);
        }}
        onSave={handleSaveMetric}
        currentUserId={currentUser?.uid}
        users={users}
        rocks={rocks}
        metricNames={metricNames}
        metricToEdit={metricToEdit}
      />
    </Container>
  );
}
